import React, { useState, useEffect, useCallback } from 'react';
import {
  Briefcase, ChevronLeft, Check, X, MapPin, Clock, ExternalLink,
  Mail, Phone, Loader2, Trash2
} from 'lucide-react';
import { educationJobs } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

// Moderation queue for user-submitted job listings.
//
// Every listing a signed-in user posts arrives here as `pending` and stays
// invisible to the public until approved — enforced in the database, not here
// (supabase/migration-education-job-postings.sql). This screen is the human
// half of that: read it, approve it, or reject it with a reason the poster
// will see on their own listing.

const FILTERS = [
  { key: 'pending', label: 'Awaiting review' },
  { key: 'approved', label: 'Live' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

const STATUS_BADGE = {
  pending: 'bg-amber-100 text-amber-800 ring-amber-200',
  approved: 'bg-brand-100 text-brand-800 ring-brand-200',
  rejected: 'bg-red-100 text-red-800 ring-red-200',
};

export const AdminEducationJobsPage = ({ setPage }) => {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [filter, setFilter] = useState('pending');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [note, setNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await educationJobs.getForReview(filter);
    setJobs(data || []);
    setLoading(false);
  }, [filter]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  if (authLoading) {
    return (
      <div className="py-20 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-brand-500 border-t-transparent mx-auto" />
      </div>
    );
  }

  // The database refuses the write regardless; this is so an admin who lost
  // their role does not stare at an empty screen wondering why nothing loads.
  if (!isAdmin) {
    return (
      <div className="py-20 text-center">
        <h1 className="text-xl font-bold text-ink mb-2">Administrators only</h1>
        <button onClick={() => setPage('home')} className="text-brand-700 font-semibold hover:underline">
          Back to the site
        </button>
      </div>
    );
  }

  const decide = async (job, status, reason = null) => {
    setBusyId(job.id);
    await educationJobs.moderate(job.id, status, user.id, reason);
    setBusyId(null);
    setRejectingId(null);
    setNote('');
    load();
  };

  const remove = async (job) => {
    if (!window.confirm(`Delete "${job.title}" permanently?`)) return;
    setBusyId(job.id);
    await educationJobs.delete(job.id);
    setBusyId(null);
    load();
  };

  return (
    <div className="w-full">
      <button
        onClick={() => setPage('admin')}
        className="flex items-center gap-2 text-ink-muted hover:text-ink mb-4 font-medium"
      >
        <ChevronLeft className="w-5 h-5" aria-hidden="true" /> Admin
      </button>

      <div className="bg-gradient-to-br from-brand-700 to-brand-900 rounded-2xl p-6 sm:p-8 text-white mb-6">
        <Briefcase className="w-10 h-10 mb-3" aria-hidden="true" />
        <h1 className="text-2xl sm:text-3xl font-black mb-1">Job Listings</h1>
        <p className="text-white/85">Review positions submitted by schools and employers</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`px-4 min-h-[40px] rounded-xl text-sm font-semibold transition-colors ${
              filter === f.key
                ? 'bg-brand-700 text-white'
                : 'bg-canvas-raised text-ink-muted ring-1 ring-hairline hover:bg-brand-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-500 border-t-transparent mx-auto" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="py-16 text-center bg-canvas rounded-2xl ring-1 ring-hairline">
          <Briefcase className="w-10 h-10 text-ink-subtle mx-auto mb-3" aria-hidden="true" />
          <p className="text-ink-muted">Nothing in this queue.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {jobs.map(job => (
            <article key={job.id} className="bg-canvas-raised rounded-2xl p-5 ring-1 ring-hairline shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="font-bold text-ink text-[17px] leading-tight">{job.title}</h2>
                  <p className="text-sm text-ink-muted mt-0.5">{job.employer_name || '—'}</p>
                </div>
                <span className={`flex-shrink-0 text-xs font-semibold px-2.5 py-1 rounded-full ring-1 ${STATUS_BADGE[job.status] || ''}`}>
                  {job.status}
                </span>
              </div>

              <div className="flex flex-wrap gap-2 mt-3 text-xs text-ink-muted">
                {job.parish && (
                  <span className="inline-flex items-center gap-1 bg-canvas px-2.5 py-1 rounded-full ring-1 ring-hairline">
                    <MapPin className="w-3 h-3" aria-hidden="true" /> {job.parish}
                  </span>
                )}
                {job.job_type && (
                  <span className="inline-flex items-center gap-1 bg-canvas px-2.5 py-1 rounded-full ring-1 ring-hairline">
                    <Clock className="w-3 h-3" aria-hidden="true" /> {job.job_type.replace('_', ' ')}
                  </span>
                )}
                {job.role_type && (
                  <span className="bg-canvas px-2.5 py-1 rounded-full ring-1 ring-hairline">{job.role_type}</span>
                )}
                {job.salary_range && (
                  <span className="bg-canvas px-2.5 py-1 rounded-full ring-1 ring-hairline">{job.salary_range}</span>
                )}
              </div>

              {job.description && (
                <p className="text-[15px] text-ink-muted leading-relaxed mt-3 whitespace-pre-line">{job.description}</p>
              )}
              {job.requirements && (
                <p className="text-sm text-ink-muted leading-relaxed mt-2 whitespace-pre-line">
                  <span className="font-semibold text-ink">Requirements: </span>{job.requirements}
                </p>
              )}

              <div className="flex flex-wrap gap-4 mt-3 text-sm">
                {job.apply_url && (
                  <a href={job.apply_url} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center gap-1.5 text-brand-700 hover:underline break-all">
                    <ExternalLink className="w-4 h-4 flex-shrink-0" aria-hidden="true" /> {job.apply_url}
                  </a>
                )}
                {job.contact_email && (
                  <span className="inline-flex items-center gap-1.5 text-ink-muted">
                    <Mail className="w-4 h-4" aria-hidden="true" /> {job.contact_email}
                  </span>
                )}
                {job.contact_phone && (
                  <span className="inline-flex items-center gap-1.5 text-ink-muted">
                    <Phone className="w-4 h-4" aria-hidden="true" /> {job.contact_phone}
                  </span>
                )}
              </div>

              {job.moderation_note && (
                <p className="mt-3 text-sm text-ink-muted bg-canvas rounded-xl p-3 ring-1 ring-hairline">
                  <span className="font-semibold text-ink">Previous note: </span>{job.moderation_note}
                </p>
              )}

              {rejectingId === job.id ? (
                <div className="mt-4 pt-4 border-t border-hairline">
                  <label className="block text-sm font-semibold text-ink mb-1.5" htmlFor={`note-${job.id}`}>
                    Why is this being rejected? The poster sees this.
                  </label>
                  <textarea
                    id={`note-${job.id}`}
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl bg-canvas-raised ring-1 ring-hairline text-[15px] focus:outline-none focus:ring-2 focus:ring-brand-500"
                    placeholder="e.g. No way to apply, or the role is not education-related"
                  />
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      onClick={() => decide(job, 'rejected', note.trim() || null)}
                      disabled={busyId === job.id}
                      className="inline-flex items-center gap-1.5 px-4 min-h-[40px] rounded-xl text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-60"
                    >
                      {busyId === job.id && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                      Confirm rejection
                    </button>
                    <button
                      onClick={() => { setRejectingId(null); setNote(''); }}
                      className="px-4 min-h-[40px] rounded-xl text-sm font-semibold text-ink-muted ring-1 ring-hairline hover:bg-canvas"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 pt-4 border-t border-hairline flex flex-wrap gap-2">
                  {job.status !== 'approved' && (
                    <button
                      onClick={() => decide(job, 'approved')}
                      disabled={busyId === job.id}
                      className="inline-flex items-center gap-1.5 px-4 min-h-[40px] rounded-xl text-sm font-semibold text-white bg-brand-700 hover:bg-brand-800 disabled:opacity-60"
                    >
                      {busyId === job.id ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : <Check className="w-4 h-4" aria-hidden="true" />}
                      Approve
                    </button>
                  )}
                  {job.status !== 'rejected' && (
                    <button
                      onClick={() => setRejectingId(job.id)}
                      className="inline-flex items-center gap-1.5 px-4 min-h-[40px] rounded-xl text-sm font-semibold text-red-700 bg-red-50 ring-1 ring-red-200 hover:bg-red-100"
                    >
                      <X className="w-4 h-4" aria-hidden="true" /> Reject
                    </button>
                  )}
                  <button
                    onClick={() => remove(job)}
                    className="inline-flex items-center gap-1.5 px-4 min-h-[40px] rounded-xl text-sm font-semibold text-ink-muted ring-1 ring-hairline hover:bg-red-50 hover:text-red-700 hover:ring-red-200"
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" /> Delete
                  </button>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminEducationJobsPage;
